import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import ContactFormDialog from './ContactFormDialog';

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const [activeCategory, setActiveCategory] = useState(0);
  const navigate = useNavigate();

  const megaMenuData = [
    {
      category: 'Trademark Services',
      services: [
        { label: 'Register Your Trademark', path: '/services/trademark-registration', description: 'Secure brand rights with thorough searches and expert registration services.' },
        { label: 'Clearance Search Services', path: '/services/clearance-search-services', description: 'Ensure brand uniqueness with comprehensive clearance searches.' },
        { label: 'Trademark Ownership Transfers', path: '/services/trademark-ownership-transfers', description: 'Smoothly transfer trademark ownership with our expert guidance.' },
        { label: 'Submission of Statements of Use', path: '/services/statement-of-use', description: 'Compliantly file Statements of Use for active trademark status.' },
        { label: 'Support and Response for Trademark Office Actions', path: '/services/trademark-office-actions', description: 'Navigate challenges with strategic responses to trademark office actions.' },
        { label: 'Active Monitoring for Trademark infringement', path: '/services/trademark-monitoring', description: 'Vigilantly monitor trademarks to protect against potential infringements.' },
        { label: 'File TTAB Petition', path: '/services/ttab-petition', description: 'Trust us to handle your TTAB petition safely and Professionally.' },
      ],
    },
    {
      category: 'Company Formation Services',
      services: [
        { label: 'Limited Liability Company', path: '/services/llc-formation', description: 'Form your LLC with professional guidance and liability protection.' },
        { label: 'C Corporation', path: '/services/c-corporation', description: 'Establish a C Corp structure for unlimited growth potential.' },
        { label: 'S Corporation', path: '/services/s-corporation', description: 'Optimize taxes with S Corporation formation and benefits.' },
        { label: 'Non-Profit', path: '/services/non-profit', description: 'Create your non-profit organization with tax-exempt status.' },
      ],
    },
    {
      category: 'Copyright Services',
      services: [
        { label: 'Register Your Copyright', path: '/services/copyright-registration', description: 'Protect your creative works with official copyright registration.' },
        { label: 'Assistance for Your Copyright Enforcement', path: '/services/copyright-enforcement', description: 'Stop unauthorized use with professional enforcement services.' },
        { label: 'Copyright Transfers and Licensing Support', path: '/services/copyright-transfers', description: 'Navigate transfers and licensing with expert legal support.' },
        { label: 'Legal Representation for Your Copyright', path: '/services/copyright-legal-representation', description: 'Expert litigation support for copyright disputes and protection.' },
        { label: 'Management of Your Copyright Portfolio', path: '/services/copyright-portfolio-management', description: 'Comprehensive management to maximize copyright value.' },
        { label: 'Compliance with DMCA Act', path: '/services/dmca-compliance', description: 'Ensure DMCA compliance and protect your online content.' },
      ],
    },
    {
      category: 'Patent Services',
      services: [
        { label: 'Search for Existing Patent', path: '/services/patent-search', description: 'Comprehensive patent searches to assess patentability.' },
        { label: 'Support and Response for Patent Office Actions', path: '/services/patent-office-actions', description: 'Expert responses to overcome USPTO objections.' },
        { label: 'Application Submission for Patent', path: '/services/patent-application', description: 'Professional patent application drafting and filing.' },
        { label: 'Legal Representation for Patent Protection', path: '/services/patent-legal-representation', description: 'Experienced attorneys for patent litigation and enforcement.' },
      ],
    },
    {
      category: 'Legal Services',
      services: [
        { label: 'Business Immigration Law', path: '/services/business-immigration-law', description: 'Navigate immigration requirements for global talent.' },
        { label: 'Due Diligence Process', path: '/services/due-diligence-process', description: 'Comprehensive due diligence for transactions and investments.' },
        { label: 'IT & Commercial Law', path: '/services/it-commercial-law', description: 'Expert legal services for technology and commercial matters.' },
        { label: 'Legal Services on Partnerships & LLPs', path: '/services/partnerships-llps', description: 'Professional formation and management of partnerships.' },
        { label: 'Data Protection & Privacy Law', path: '/services/data-protection-privacy', description: 'GDPR and CCPA compliance with expert privacy guidance.' },
      ],
    },
    {
      category: 'Additional Corporate Services',
      services: [
        { label: 'Amazon Brand Registry in the US', path: '/services/amazon-brand-registry', description: 'Protect your brand on Amazon with registry services.' },
        { label: 'Bookkeeping Service', path: '/services/bookkeeping-service', description: 'Professional bookkeeping to keep finances organized.' },
        { label: 'Business Formation Services', path: '/services/business-formation-services', description: 'Complete formation services for all entity types.' },
        { label: 'Certificate of Good Standing', path: '/services/certificate-good-standing', description: 'Obtain official proof of business compliance.' },
        { label: 'Company Dissolution', path: '/services/company-dissolution', description: 'Properly and legally dissolve your business.' },
        { label: 'Company Name Change', path: '/services/company-name-change', description: 'Seamlessly change your company name legally.' },
        { label: 'Company Renewal Services', path: '/services/company-renewal', description: 'Stay compliant with annual renewal filings.' },
        { label: 'Issue Of Shares', path: '/services/issue-of-shares', description: 'Professional share issuance and capital raising services.' },
        { label: 'Nominee Director & Secretarial Services', path: '/services/nominee-director-secretarial', description: 'Professional director and secretarial services.' },
        { label: 'Tax Advisory & Filing', path: '/services/tax-advisory-filing', description: 'Strategic tax planning and filing services.' },
        { label: 'Transfer of Shares', path: '/services/transfer-of-shares', description: 'Expert assistance with share transfer documentation.' },
        { label: 'CFO Services', path: '/services/cfo-services', description: 'Fractional CFO services for strategic financial leadership.' },
      ],
    },
  ];

  const menuItems = [
    { label: 'Home', path: '/' },
    { label: 'About Us', path: '/about' },
    { label: 'Services', hasMegaMenu: true },
    { label: 'Contact Us', path: '/contact' },
  ];

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 shadow-md" style={{background: '#ffffff'}}>
        {/* Top Bar Strip */}
        <div className="bg-[#0a2d5e] text-white text-[13px] max-[576px]:text-[11px] py-2">
          <div className="container mx-auto px-4 flex items-center justify-end max-[576px]:justify-center gap-6 max-[576px]:gap-3">
            <div className="flex items-center gap-2">
              <Phone size={14} className="flex-shrink-0" />
              <a href="tel:+1 (877) 946-4330" className="hover:underline">+1 (877) 946-4330</a>
            </div>
            <div className="flex items-center gap-2 max-[576px]:hidden">
              <Mail size={14} className="flex-shrink-0" />
              <a href="mailto:info@ipprotectionassociate.com" className="hover:underline">info@ipprotectionassociate.com</a>
            </div>
            <div className="flex items-center gap-2 max-[767px]:hidden">
              <MapPin size={14} className="flex-shrink-0" />
              <span>Old Middlefield Way, Mountain View, CA 94043</span>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2" data-testid="nav-logo">
              <img src="/assets/img/logo-color.png" width="200" alt="IP Protection Associate Logo" />
              
            </Link>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-8">
              {menuItems.map((item, index) => (
                <div key={index} className="relative">
                  {item.hasMegaMenu ? (
                    <button
                      className="text-[#bf8a60] font-medium transition-colors"
                      data-testid="nav-services-menu"
                      onMouseEnter={() => setShowMegaMenu(true)}
                    >
                      {item.label}
                    </button>
                  ) : (
                    <Link
                      to={item.path}
                      className="text-[#bf8a60] font-medium transition-colors"
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
              <button
                onClick={() => setShowDialog(true)}
                className="bg-[#bf8a60] text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#a0733e] transition-all transform hover:scale-105 shadow-md"
                data-testid="nav-get-started-btn"
              >
                Register My Trademark
              </button>
              <button className="js-chat border-2 border-[#bf8a60] text-[#bf8a60] px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#bf8a60] hover:text-white transition-all" data-testid="nav-chat-now-btn">
                Chat Now
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-[#bf8a60]"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isOpen && (
            <div className="lg:hidden pb-6 space-y-4 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
              {menuItems.map((item, index) => (
                <div key={index}>
                  {item.hasMegaMenu ? (
                    <div>
                      <div className="text-[#2C1810] font-medium py-2">{item.label}</div>
                      {megaMenuData.map((category, catIndex) => (
                        <div key={catIndex} className="pl-2 mb-4">
                          <Link
                            to={category.services[0].path}
                            className="text-[#06214c] font-semibold text-sm py-2 block hover:underline"
                            onClick={() => setIsOpen(false)}
                          >
                            {category.category}
                          </Link>
                          <div className="pl-2 space-y-1">
                            {category.services.map((service, serviceIndex) => (
                              <Link
                                key={serviceIndex}
                                to={service.path}
                                className="block text-[#2C1810] hover:text-[#06214c] py-1.5 text-sm transition-colors"
                                onClick={() => setIsOpen(false)}
                              >
                                {service.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className="block text-[#2C1810] font-medium hover:text-[#06214c] py-2 transition-colors"
                      onClick={() => setIsOpen(false)}
                      data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
              <div className="space-y-3 pt-4">
                <button
                  onClick={() => {
                    setShowDialog(true);
                    setIsOpen(false);
                  }}
                  className="btn-primary w-full"
                  data-testid="mobile-get-started-btn"
                >
                  Get Started
                </button>
                <button className="js-chat btn-secondary w-full" data-testid="mobile-chat-now-btn">
                  Chat Now
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Mega Menu for Desktop - Dark Theme with Sidebar */}
        {showMegaMenu && (
          <div
            className="hidden lg:block absolute left-0 right-0 shadow-2xl z-40"
            style={{
              top: 'calc(100% + 0px)',
              background: '#06214c',
              backdropFilter: 'blur(10px)',
              padding: '30px 0',
            }}
            onMouseEnter={() => setShowMegaMenu(true)}
            onMouseLeave={() => {
              setShowMegaMenu(false);
              setActiveCategory(0);
            }}
          >
            <div className="container mx-auto px-6">
              <div className="flex gap-10">
                {/* Left Sidebar Navigation */}
                <div className="w-72 border-r border-white/10 pr-8">
                  <h3 className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-4">Service Categories</h3>
                  {megaMenuData.map((category, index) => (
                    <Link
                      key={index}
                      to={category.services[0].path}
                      onMouseEnter={() => setActiveCategory(index)}
                      onClick={() => setShowMegaMenu(false)}
                      className={`w-full text-left py-3.5 px-4 rounded-lg transition-all duration-200 flex items-center justify-between group mb-2 text-sm ${
                        activeCategory === index
                          ? 'bg-white/10 text-white shadow-lg'
                          : 'text-white/80 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      <span className="font-medium">{category.category}</span>
                      <svg className={`w-4 h-4 transform transition-transform ${activeCategory === index ? 'translate-x-1' : 'group-hover:translate-x-1'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  ))}
                </div>

                {/* Right Content Area with Service Details - Only Active Category */}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
                    <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display' }}>
                      {megaMenuData[activeCategory].category}
                    </h3>
                    <span className="text-white/50 text-sm">
                      {megaMenuData[activeCategory].services.length} Services
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-x-10 gap-y-7 max-h-[400px] overflow-y-auto pr-4 custom-scrollbar">
                    {megaMenuData[activeCategory].services.map((service, serviceIndex) => (
                      <Link
                        key={serviceIndex}
                        to={service.path}
                        className="block group"
                        onClick={() => setShowMegaMenu(false)}
                      >
                        <h4 className="text-white text-sm font-semibold mb-2 group-hover:text-[#D4A574] transition-colors leading-snug flex items-start">
                          <span className="flex-1">{service.label}</span>
                          <svg className="w-4 h-4 mt-0.5 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </h4>
                        <p className="text-white/60 text-xs leading-relaxed">
                          {service.description || `Professional ${service.label.toLowerCase()} services for your business.`}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>

      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="popup" />
    </>
  );
};

export default Navigation;